import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, MessageCircle, Sparkles, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  deleteNotifications,
  disableBrowserPushNotifications,
  enableBrowserPushNotifications,
  fetchNotificationPreferences,
  fetchNotifications,
  markNotificationClicked,
  markNotificationsRead,
  processNotificationDispatchQueue,
  updateNotificationPreferences,
  type NotificationInboxItem,
} from "@/lib/progress";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "@/lib/supabase/client";
import { useSupabaseSession } from "@/hooks/use-supabase-session";

type FilterMode = "all" | "unread";

function formatRelative(value: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Az önce";

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));
  if (diffMinutes < 1) return "Az önce";
  if (diffMinutes < 60) return `${diffMinutes} dk önce`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} saat önce`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} gün önce`;
  return date.toLocaleDateString("tr-TR");
}

function resolveNotificationIcon(item: NotificationInboxItem) {
  if (item.type.startsWith("message_")) return MessageCircle;
  if (item.type.startsWith("energy_") || item.type.startsWith("healing_")) return Zap;
  return Sparkles;
}

export default function UserPanelBildirimler() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSupabaseSession();
  const [filter, setFilter] = useState<FilterMode>("all");

  const notificationsQuery = useQuery({
    queryKey: ["panel-notifications"],
    queryFn: async () => {
      await processNotificationDispatchQueue().catch(() => null);
      return await fetchNotifications(100);
    },
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const preferencesQuery = useQuery({
    queryKey: ["panel-notification-preferences"],
    queryFn: async () => await fetchNotificationPreferences(),
  });

  useEffect(() => {
    const userId = String(user?.id ?? "").trim();
    if (!userId) return;

    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(`panel-notifications:${userId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, () => {
        void queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, () => {
        void queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, () => {
        void queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, user?.id]);

  const markReadMutation = useMutation({
    mutationFn: async (input: { ids?: string[]; all?: boolean }) => await markNotificationsRead(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Bildirim güncellenemedi";
      toast({ title: "Hata", description: msg });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (input: { ids?: string[]; all?: boolean }) => await deleteNotifications(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
      toast({ title: "Temizlendi", description: "Bildirimler silindi." });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Bildirimler silinemedi";
      toast({ title: "Hata", description: msg });
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: async (input: Record<string, unknown>) => await updateNotificationPreferences(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["panel-notification-preferences"] });
      toast({ title: "Kaydedildi", description: "Bildirim tercihleri güncellendi." });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Tercihler kaydedilemedi";
      toast({ title: "Hata", description: msg });
    },
  });

  const pushToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (enabled) {
        const result = await enableBrowserPushNotifications();
        if (!result.ok) {
          throw new Error(
            result.reason === "missing_vapid_key"
              ? "Push anahtarı eksik (VITE_WEB_PUSH_VAPID_PUBLIC_KEY)."
              : result.reason === "permission_denied"
                ? "Tarayıcı bildirim izni reddedildi."
                : "Push bildirimi açılamadı.",
          );
        }
      } else {
        await disableBrowserPushNotifications();
      }

      await updateNotificationPreferences({ push_enabled: enabled });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["panel-notification-preferences"] });
      toast({ title: "Güncellendi", description: "Push bildirim ayarı kaydedildi." });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Push ayarı değiştirilemedi";
      toast({ title: "Hata", description: msg });
    },
  });

  const items = useMemo(() => {
    const source = notificationsQuery.data?.items ?? [];
    return [...source].sort((a, b) => new Date(String(b.created_at ?? 0)).getTime() - new Date(String(a.created_at ?? 0)).getTime());
  }, [notificationsQuery.data?.items]);

  const unreadCount = useMemo(() => items.filter((item) => item.status === "unread").length, [items]);

  const filteredItems = useMemo(() => {
    if (filter === "unread") return items.filter((item) => item.status === "unread");
    return items;
  }, [filter, items]);

  const preferences = preferencesQuery.data;

  return (
    <div className="space-y-5">
      <Card className="rounded-3xl border border-[#decbe8] bg-gradient-to-br from-[#fff7fc] via-[#f8eefb] to-[#f1e6f7] p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-600">
              <Bell className="h-3.5 w-3.5" /> Bildirim Merkezi
            </div>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-[#241530]">Bildirimler</h2>
            <p className="mt-1 text-sm text-slate-600">Mesaj, etkileşim ve sistem gelişmelerini buradan takip edebilirsin.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={filter === "all" ? "default" : "outline"}
              className="rounded-xl"
              onClick={() => setFilter("all")}
            >
              Tümü ({items.length})
            </Button>
            <Button
              type="button"
              variant={filter === "unread" ? "default" : "outline"}
              className="rounded-xl"
              onClick={() => setFilter("unread")}
            >
              Okunmamış ({unreadCount})
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={markReadMutation.isPending || unreadCount <= 0}
              onClick={() => {
                void markReadMutation.mutate({ all: true });
              }}
            >
              <CheckCheck className="mr-2 h-4 w-4" /> Tümünü Okundu Yap
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={deleteMutation.isPending || items.length <= 0}
              onClick={() => {
                void deleteMutation.mutate({ all: true });
              }}
            >
              Tümünü Sil
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-3xl border border-[#e7d8ef] bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-500">Bildirim Tercihleri</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            { key: "allow_message", label: "Mesajlar" },
            { key: "allow_energy", label: "Enerji / Şifa" },
            { key: "allow_match", label: "Eşleşme / Davet" },
            { key: "allow_system", label: "Sistem duyuruları" },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between rounded-2xl border border-slate-200 px-3 py-2">
              <span className="text-sm font-medium text-slate-700">{item.label}</span>
              <Switch
                checked={Boolean((preferences as any)?.[item.key] ?? true)}
                onCheckedChange={(checked) => {
                  void updatePreferencesMutation.mutate({ [item.key]: checked });
                }}
              />
            </div>
          ))}

          <div className="flex items-center justify-between rounded-2xl border border-slate-200 px-3 py-2 sm:col-span-2">
            <div>
              <div className="text-sm font-medium text-slate-700">Push Bildirimleri</div>
              <div className="text-xs text-slate-500">Tarayıcı kapalıyken de bildirim almak için aç.</div>
            </div>
            <Switch
              checked={Boolean(preferences?.push_enabled ?? true)}
              onCheckedChange={(checked) => {
                void pushToggleMutation.mutate(checked);
              }}
            />
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {filteredItems.length ? (
          filteredItems.map((item) => {
            const Icon = resolveNotificationIcon(item);
            const unread = item.status === "unread";

            return (
              <button
                key={item.id}
                type="button"
                className={cn(
                  "w-full rounded-2xl border p-4 text-left transition",
                  unread
                    ? "border-[#d9bee9] bg-[#f8f1fd] shadow-[0_10px_30px_-22px_rgba(124,58,237,0.55)]"
                    : "border-slate-200 bg-white hover:bg-slate-50",
                )}
                onClick={() => {
                  void markNotificationClicked(item.id)
                    .then(async () => {
                      await queryClient.invalidateQueries({ queryKey: ["panel-notifications"] });
                    })
                    .catch(() => null);

                  const isMessageNotification = String(item.type ?? "").startsWith("message_");
                  const messagePeerId = String((item.data as any)?.fromUserId ?? item.actor_user_id ?? "").trim();
                  if (isMessageNotification && messagePeerId) {
                    const messagePeerName = String((item.data as any)?.fromDisplayName ?? item.actor_display_name ?? "").trim();
                    const nameQuery = messagePeerName ? `&name=${encodeURIComponent(messagePeerName)}` : "";
                    navigate(`/panel/kesfet/sohbet?peer=${encodeURIComponent(messagePeerId)}${nameQuery}`);
                    return;
                  }

                  if (item.deep_link) {
                    navigate(item.deep_link);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={cn("mt-0.5 grid h-8 w-8 place-items-center rounded-xl", unread ? "bg-[#e7d2f6] text-[#7b37b7]" : "bg-slate-100 text-slate-600")}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="truncate text-sm font-bold text-slate-900">{item.title}</h4>
                      {unread ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">Yeni</span> : null}
                    </div>
                    {item.body ? (
                      <p className="mt-1 text-sm text-slate-600">
                        {item.actor_display_name ? item.body.replace(/^Bir kullanıcı/i, item.actor_display_name) : item.body}
                      </p>
                    ) : null}
                    <div className="mt-2 text-xs text-slate-500">{formatRelative(item.created_at)}</div>
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <Card className="rounded-2xl border border-dashed border-primary/30 bg-white p-6 text-sm text-slate-600">
            Bu filtrede henüz bildirim yok.
          </Card>
        )}
      </div>
    </div>
  );
}
